import { ArrowLeft, PanelRightClose, Workflow } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  encodePublicDatasetRunKey,
  type SessionPublicDatasetRun,
} from "~/pages/manifold-nextgen/components/conversations/useSessionPublicDatasetRuns";
import { usePublicDatasetRunStatus } from "~/pages/manifold-nextgen/hooks/usePublicDatasetRunStatus";
import { publicDatasetRunStatusDisplay } from "./SessionContextPublicDatasetRunDetail";

interface SessionContextPublicDatasetRunListProps {
  runs: SessionPublicDatasetRun[];
  onOpenRun: (dagId: string, runId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

// Reached from the single Apps > Pipeline runs TOC entry; selecting a row
// opens its detail.
export const SessionContextPublicDatasetRunList = ({
  runs,
  onOpenRun,
  onBack,
  onCollapse,
}: SessionContextPublicDatasetRunListProps) => {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back to session context"
          className="size-8"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="truncate text-sm font-medium text-foreground">Pipeline runs</span>
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCollapse}
              aria-label="Collapse session context"
              className="ml-auto size-8"
            >
              <PanelRightClose className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Toggle Context</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {runs.length === 0 ? (
          <p className="p-4 text-center text-xs italic text-muted-foreground/80">
            No pipeline runs yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {runs.map((run) => (
              <PublicDatasetRunListRow
                key={encodePublicDatasetRunKey(run.dagId, run.runId)}
                run={run}
                onOpen={onOpenRun}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Polls its own status independently of any active turn, so the list stays
// live while open.
const PublicDatasetRunListRow = ({
  run,
  onOpen,
}: {
  run: SessionPublicDatasetRun;
  onOpen: (dagId: string, runId: string) => void;
}) => {
  const { data } = usePublicDatasetRunStatus(run.dagId, run.runId);
  const display = publicDatasetRunStatusDisplay(data?.state);
  const label = run.dagDisplayName ?? run.dagId;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(run.dagId, run.runId)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-accent/50"
      >
        <Workflow className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 text-xs font-medium",
            display.isFailed
              ? "text-destructive"
              : display.isCompleted
                ? "text-muted-foreground"
                : "text-primary"
          )}
        >
          {display.isLive && (
            <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />
          )}
          {display.label}
        </span>
      </button>
    </li>
  );
};
